export const TEAM_OPTIONS = [
  { value: "all", label: "All teams" },
  { value: "a", label: "Team A" },
  { value: "b", label: "Team B" },
  { value: "c", label: "Team C" },
];

const VALID_CODES = new Set(["a", "b", "c", "all"]);

export function normalizeTeamCode(raw) {
  const t = String(raw || "").trim().toLowerCase();
  if (t === "teama" || t === "team a") return "a";
  if (t === "teamb" || t === "team b") return "b";
  if (t === "teamc" || t === "team c") return "c";
  if (t === "other" || t === "unassign" || t === "unassigned") return "c";
  return t;
}

export function parseUserTeams(raw) {
  const s = String(raw || "").trim().toLowerCase();
  if (!s) return [];
  if (s === "all") return ["all"];
  const parts = s
    .split(/[,;|]+/)
    .map(normalizeTeamCode)
    .filter((c) => VALID_CODES.has(c) && c !== "all");
  return [...new Set(parts)];
}

export function serializeUserTeams(input) {
  if (input == null) return "";
  if (Array.isArray(input)) {
    const codes = input.map(normalizeTeamCode).filter((c) => VALID_CODES.has(c));
    if (codes.includes("all")) return "all";
    return [...new Set(codes.filter((c) => c !== "all"))].join(",");
  }
  const parsed = parseUserTeams(input);
  if (parsed.includes("all")) return "all";
  return parsed.join(",");
}

export function userHasAllTeams(raw) {
  return parseUserTeams(raw).includes("all");
}

export function userHasMultipleTeams(raw) {
  const codes = parseUserTeams(raw);
  return codes.includes("all") || codes.length > 1;
}

export function teamsOverlap(a, b) {
  const ta = parseUserTeams(a);
  const tb = parseUserTeams(b);
  if (!ta.length || !tb.length) return false;
  if (ta.includes("all") || tb.includes("all")) return true;
  return ta.some((c) => tb.includes(c));
}

const TEAM_LABELS = {
  a: "Team A",
  b: "Team B",
  c: "Team C",
  all: "All teams",
};

export function formatUserTeams(raw) {
  const codes = parseUserTeams(raw);
  if (!codes.length) return "—";
  if (codes.includes("all")) return TEAM_LABELS.all;
  return codes.map((c) => TEAM_LABELS[c] || c).join(", ");
}

/** Plan Management tab keys the user may see. */
export function visiblePlanTeamTabs(raw) {
  const ALL_TABS = [
    { key: "team a", label: "TEAM A", code: "a" },
    { key: "team b", label: "TEAM B", code: "b" },
    { key: "unassign", label: "TEAM C", code: "c" },
    { key: "history", label: "HISTORY", code: null },
  ];
  const codes = parseUserTeams(raw);
  const historyTab = ALL_TABS.find((t) => t.key === "history");
  const teamTabs = ALL_TABS.filter((t) => t.key !== "history");
  if (!codes.length || codes.includes("all")) return ALL_TABS;
  const tabs = teamTabs.filter((t) => codes.includes(t.code));
  const visible = tabs.length ? tabs : [teamTabs[0]];
  return [...visible, historyTab];
}

export function defaultPlanTeamTabKey(raw) {
  const tabs = visiblePlanTeamTabs(raw);
  return tabs[0]?.key || "team a";
}

export function teamCodesForPersonFilter(raw) {
  const codes = parseUserTeams(raw);
  if (codes.includes("all")) return ["a", "b", "c"];
  return codes.filter((c) => c !== "all");
}

const DASHBOARD_TEAM_FILTER_OPTIONS = [
  { value: "all", label: "ALL Team" },
  { value: "a", label: "Team A" },
  { value: "b", label: "Team B" },
  { value: "c", label: "Team C" },
];

function normalizeRoleName(raw) {
  return String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

export function userRoleNames(user) {
  const roles = user?.roles || [];
  if (roles.length) {
    return roles.map((r) => normalizeRoleName(r.roleName || r.role_name));
  }
  if (user?.roleName || user?.role_name) {
    return [normalizeRoleName(user.roleName || user.role_name)];
  }
  return [];
}

/** Admin, lead, manager, PM may use ALL Team on Dashboard. */
export function canViewAllDashboardTeams(user) {
  return userRoleNames(user).some((name) => {
    if (!name) return false;
    if (name === "admin" || name === "manager" || name === "pm") return true;
    if (name === "lead" || name.includes("lead")) return true;
    return false;
  });
}

export function userTeamSource(user) {
  if (user?.teams?.length) return user.teams;
  return parseUserTeams(user?.team);
}

export function dashboardAssignableTeamCodes(user) {
  if (canViewAllDashboardTeams(user)) return ["a", "b", "c"];
  const codes = userTeamSource(user);
  if (codes.includes("all")) return ["a", "b", "c"];
  return codes.filter((c) => c !== "all" && VALID_CODES.has(c));
}

export function buildDashboardTeamFilterOptions(user) {
  const assignable = new Set(dashboardAssignableTeamCodes(user));
  const canAll = canViewAllDashboardTeams(user);

  return DASHBOARD_TEAM_FILTER_OPTIONS.map((opt) => ({
    ...opt,
    disabled: opt.value === "all" ? !canAll : !canAll && !assignable.has(opt.value),
  }));
}

export function defaultDashboardTeamFilter(user) {
  const options = buildDashboardTeamFilterOptions(user);
  const firstEnabled = options.find((opt) => !opt.disabled);
  return firstEnabled?.value || "a";
}

export function isDashboardTeamFilterAllowed(user, teamFilter) {
  const options = buildDashboardTeamFilterOptions(user);
  const match = options.find((opt) => opt.value === teamFilter);
  return !!match && !match.disabled;
}

export function toggleTeamSelection(current, value) {
  const code = normalizeTeamCode(value);
  let next = (current || []).map(normalizeTeamCode);
  if (code === "all") {
    return next.includes("all") ? [] : ["all"];
  }
  next = next.filter((c) => c !== "all");
  if (next.includes(code)) next = next.filter((c) => c !== code);
  else next.push(code);
  return next;
}
